import DataQuery from "data/DataQuery";
import { Dashboard_Consumption, Fueling_FuelingHistory } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import {
  Dashboard_DashboardMeasuredDataCard_Electricity,
  Dashboard_DashboardMeasuredDataCard_ElectricityProduction,
  Dashboard_DashboardMeasuredDataCard_Gas,
  Dashboard_DashboardMeasuredDataCard_Heat,
  Dashboard_DashboardMeasuredDataCard_Charging,
} from "features/shared/components/loaders/promiseLoadingAreas";
import { getRouteParams } from "features/shared/helpers/routeParams";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import React from "react";
import { useSelector } from "react-redux";
import { getConsumptionParams } from "features/shared/helpers/queryParams";
import DashboardMeasuredDataCard from "features/dashboard/components/DashboardMeasuredDataCard";
import MeasuredDataResultDTO from "features/shared/dtos/MeasuredDataResultDTO";
import { ConsumptionType } from "features/consumption/enums/consumptionType";
import DashboardSectionProps from "features/dashboard/models/DashboardSectionProps";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IDashboardService from "../services/IDashboardService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { useTranslation } from "react-i18next";
import { containsFuelingCommodity } from "features/contracts/helpers/commodityDetailHelper";
import { endOfYear, formatISO, startOfYear, sub } from "date-fns";
import { QuotationType } from "features/shared/enums/quotationType";
import DashboardEmptyCommodityConfig from "features/dashboard/models/DashboardEmptyCommodityConfig";
import ContractsCountState from "infrastructure/store/contractsCount/ContractsCountState";

interface Configuration {
  commodityDetail: CommodityDetail;
  loaderArea: string;
  url: string;
  isProduction?: boolean;
}

const MeasuredDataTypesMap: Record<string, Configuration> = {
  electricity: {
    commodityDetail: "ElectricityCustomer",
    loaderArea: Dashboard_DashboardMeasuredDataCard_Electricity,
    url: Dashboard_Consumption,
  },
  gas: {
    commodityDetail: "GasCustomer",
    loaderArea: Dashboard_DashboardMeasuredDataCard_Gas,
    url: Dashboard_Consumption,
  },
  heat: {
    commodityDetail: "HeatCustomer",
    loaderArea: Dashboard_DashboardMeasuredDataCard_Heat,
    url: Dashboard_Consumption,
  },
  electricityProduction: {
    commodityDetail: "ElectricityCustomer",
    loaderArea: Dashboard_DashboardMeasuredDataCard_ElectricityProduction,
    url: Dashboard_Consumption,
    isProduction: true,
  },
  charging: {
    commodityDetail: "eMobility",
    loaderArea: Dashboard_DashboardMeasuredDataCard_Charging,
    url: Fueling_FuelingHistory,
  },
};

const DashboardMeasuredDataSection = (props: DashboardSectionProps) => {
  // translations
  const t = useTranslation("dashboard\\dashboard").t;

  // DI
  const dashboardService = useInjection<IDashboardService>(
    IocTypes.DashboardService
  );

  // private props
  const configuration = dashboardService.getConfiguration<Configuration>(
    props.additionalInfo!,
    "type",
    MeasuredDataTypesMap,
    "Consumption"
  );
  const emptyCommodityConfig =
    dashboardService.getAdditionalInfoObj<DashboardEmptyCommodityConfig>(
      props.additionalInfo!,
      "emptyCommodityConfig"
    );

  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  const contractsCount = useSelector<IAppState, ContractsCountState>(
    (store) => store.contractsCount
  ).contractsCount;

  // helpers
  const getConsumptionQuery = (type: ConsumptionType): DataQuery => {
    return {
      url: configuration.url,
      routeParams: getRouteParams("customerId", customer.customerId!),
      params: getMeasuredDataParams(type),
    };
  };

  const getMeasuredDataParams = (type: ConsumptionType): URLSearchParams => {
    if (containsFuelingCommodity([configuration.commodityDetail])) {
      return new URLSearchParams({
        From: formatISO(getFromDate(), { representation: "date" }),
        To: formatISO(getToDate(), { representation: "date" }),
        QuotationType: QuotationType.Year,
        CustomerId: customer.customerId!,
      });
    } else {
      return getConsumptionParams(
        type,
        configuration.commodityDetail,
        props.itemsCount ?? 5,
        configuration.isProduction
      );
    }
  };

  const getToDate = () => {
    let actualDate = new Date();
    return endOfYear(actualDate);
  };

  const getFromDate = () => {
    let yearsCount = props.itemsCount ?? 5;

    let endOfActualMonth = getToDate();
    return startOfYear(sub(endOfActualMonth, { years: yearsCount - 1 }));
  };

  const hasPlanMeasuredData = (): boolean => {
    if (
      containsFuelingCommodity([configuration.commodityDetail]) || configuration.isProduction
    ) {
      return false;
    }

    return true;
  };

  const isLoadedData = () => {
    let isLoadedRealData = consumptionRealStatus == "AfterFirstLoad";
    let isLoadedPlanData = hasPlanMeasuredData()
      ? consumptionPlanStatus === "AfterFirstLoad"
      : true;

    return isLoadedRealData && isLoadedPlanData;
  };

  // fetch data
  // real consumption
  const { data: consumptionReal, loadStatus: consumptionRealStatus } =
    useQuery<MeasuredDataResultDTO>(
      getConsumptionQuery("Real"),
      {},
      [customer],
      {
        useTracker: true,
        area: configuration.loaderArea,
      }
    );
  // plan consumption
  const { data: consumptionPlan, loadStatus: consumptionPlanStatus } =
    useQuery<MeasuredDataResultDTO>(
      getConsumptionQuery("Plan"),
      {},
      [customer],
      {
        useTracker: true,
        area: configuration.loaderArea,
      },
      {
        apply: true,
        condition: () => hasPlanMeasuredData(),
      }
    );

  // sub-renders
  const renderGrapCard = (noGraphContent?: () => JSX.Element) => {
    return (
        <DashboardMeasuredDataCard
          title={t(`dashboardItems.${props.title}`)}
          icon={props.icon}
          iconColor={props.iconColor}
          background={props.background}
          realData={consumptionReal}
          planData={hasPlanMeasuredData() ? consumptionPlan : undefined}
          isLoadedData={isLoadedData()}
          loaderArea={configuration.loaderArea}
          headerClassName="text-white"
          commodityDetail={configuration.commodityDetail}
          isProduction={configuration.isProduction}
          renderNoGraphContent={noGraphContent}
        />
    );
  };

  const renderEmptyCommodityInfo = (info?: string) => {
    return <div dangerouslySetInnerHTML={{ __html: info || "" }} className="h-100" />;
  };

  const renderResult = () => {
    let commodityCount = contractsCount.find(
      (x) => x.commodityDetail == configuration.commodityDetail
    );

    if (
      commodityCount &&
      commodityCount.count == 0 &&
      emptyCommodityConfig != null
    ) {
      if (emptyCommodityConfig.showType == "info") {
        return renderGrapCard(() =>
          renderEmptyCommodityInfo(emptyCommodityConfig.content)
        );
      } else {
        return null;
      }
    } else {
      return renderGrapCard();
    }
  };

  // render
  return renderResult();
};

export default DashboardMeasuredDataSection;
